import React, { useMemo, useState } from "react";
import { Box, Stack, Avatar } from "@mui/material";
import { useNavigate } from "react-router-dom";

import EmptyState from "../../../../../component/EmptyState";
import SectionLoader from "../../../../../component/SectionLoader";
import TreeTable from "../../../../../component/TreeTable";
import PrimaryButton from "../../../../../component/PrimaryButton";
import {
  useOrgEmployees,
  useOrgDepartments,
  useOrgGroups,
} from "../../../../../services/useOrgData";
import { tokens, displayFont } from "../../../../../theme";
import { buildOrgTree, flattenOrgTree, countOrgTree, allUnitIds, INDENT_PX } from "./orgTree";

/**
 * Organisation, as a chart and as a list.
 *
 * The book's claim is that the chart *is* the object: the headcounts sit on the
 * nodes rather than in stat tiles above them, so structure and size are read in
 * one pass instead of two. And list view is not the People table with a
 * department column — it is the same tree, indented, one row per unit, opened
 * to whatever depth the reader asked for. Both read one `buildOrgTree` result,
 * so the two views cannot disagree about a number.
 */

const VACANT = "Vacant";

/* ── chart ─────────────────────────────────────────────────────────────── */

const NodeShell = ({ dashed, children, sx }) => (
  <Box
    sx={{
      width: "100%",
      boxSizing: "border-box",
      background: tokens.card,
      // Dashed is an unfilled role. Never red: a vacancy is a fact about the
      // org, not something that went wrong.
      border: `1px ${dashed ? "dashed" : "solid"} ${tokens.borderStrong}`,
      borderRadius: "10px",
      p: "0.875rem 1rem",
      textAlign: "center",
      ...sx,
    }}
  >
    {children}
  </Box>
);

// 2px, strong border, and only ever horizontal or vertical — the book allows no
// diagonals, so every join is an elbow made of two rules.
const rule = { background: tokens.borderStrong };

const DepartmentNode = ({ unit, index, count, onOpen }) => {
  const vacant = unit.kind === "department" && !unit.lead;
  const leadLabel = unit.lead ? unit.lead.name : unit.kind === "department" ? VACANT : "—";
  return (
    <Box sx={{ position: "relative", px: "0.75rem" }}>
      {/* the sibling rail: the first node draws only its right half, the last
          only its left, so the rail stops at the outermost children */}
      {count > 1 && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            height: "2px",
            ...rule,
            left: index === 0 ? "50%" : 0,
            right: index === count - 1 ? "50%" : 0,
          }}
        />
      )}
      <Box sx={{ position: "absolute", top: 0, left: "calc(50% - 1px)", width: "2px", height: "22px", ...rule }} />
      <Box sx={{ mt: "22px", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <NodeShell
          dashed={vacant}
          sx={{
            cursor: "pointer",
            transition: (theme) =>
              theme.transitions.create("border-color", { duration: theme.transitions.duration.shortest }),
            "&:hover": { borderColor: tokens.primary },
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={onOpen}
            sx={{
              all: "unset", display: "block", width: "100%", cursor: "pointer",
              fontSize: "0.875rem", fontWeight: 600, color: tokens.text,
            }}
          >
            {unit.name}
          </Box>
          <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted, mt: "2px", ...(vacant ? { fontStyle: "italic" } : {}) }}>
            {leadLabel} · {unit.headcount}
          </Box>
        </NodeShell>
        {unit.children.length > 0 && (
          <>
            <Box sx={{ width: "2px", height: "16px", ...rule }} />
            <Stack sx={{ width: "100%" }} spacing={0.75}>
              {unit.children.map((team) => (
                <Stack
                  key={team.id}
                  direction="row"
                  justifyContent="space-between"
                  sx={{
                    background: tokens.card,
                    border: `1px solid ${tokens.border}`,
                    borderRadius: "8px",
                    p: "0.5625rem 0.75rem",
                    fontSize: "0.75rem",
                    color: tokens.text,
                  }}
                >
                  <Box component="span">{team.name}</Box>
                  <Box component="span" sx={{ color: tokens.textMuted }}>{team.headcount}</Box>
                </Stack>
              ))}
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
};

const ChartView = ({ root, onOpenUnit }) => (
  <Box
    sx={{
      background: tokens.surface,
      border: `1px solid ${tokens.border}`,
      borderRadius: "10px",
      p: "2.5rem 2rem",
      overflowX: "auto",
    }}
  >
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: `${root.children.length * 13}rem` }}>
      <Box
        sx={{
          background: tokens.primary,
          color: tokens.onPrimary,
          borderRadius: "10px",
          p: "0.875rem 1.375rem",
          textAlign: "center",
          minWidth: "12.5rem",
        }}
      >
        <Box sx={{ fontSize: "0.875rem", fontWeight: 600 }}>{root.lead ? root.lead.name : "Whole company"}</Box>
        <Box sx={{ fontSize: "0.75rem", opacity: 0.85, mt: "2px" }}>
          {root.lead?.role ? `${root.lead.role} · ` : ""}{root.headcount}
        </Box>
      </Box>
      {root.children.length > 0 && (
        <>
          <Box sx={{ width: "2px", height: "26px", ...rule }} />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${root.children.length}, minmax(11rem, 1fr))`,
              width: "100%",
            }}
          >
            {root.children.map((unit, i) => (
              <DepartmentNode
                key={unit.id}
                unit={unit}
                index={i}
                count={root.children.length}
                onOpen={() => onOpenUnit(unit)}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  </Box>
);

/* ── list ──────────────────────────────────────────────────────────────── */

const COLUMNS = [
  { key: "name", label: "Department and team", width: "1fr" },
  { key: "lead", label: "Led by", width: "1.2fr" },
  { key: "people", label: "People", width: "5.625rem", align: "right" },
  { key: "contains", label: "Contains", width: "8.125rem" },
];

const numberSx = { fontVariantNumeric: "tabular-nums" };

const Twisty = ({ row }) => (
  <Box
    component="span"
    aria-hidden
    sx={{ color: tokens.textMuted, fontSize: "0.6875rem", width: "0.625rem", flex: "none" }}
  >
    {row.kind === "unit" && row.expandable ? (row.expanded ? "▾" : "▸") : ""}
  </Box>
);

const listCell = (row, col) => {
  if (col.key === "name") {
    if (row.kind === "person") {
      const initials = row.name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
      return (
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Twisty row={row} />
          <Avatar
            src={row.profileImage || undefined}
            sx={{ width: "1.5rem", height: "1.5rem", bgcolor: tokens.primary, color: tokens.onPrimary, fontSize: "0.625rem", fontWeight: 600 }}
          >
            {initials}
          </Avatar>
          <Box component="span" sx={{ fontWeight: 400 }}>{row.name}</Box>
        </Stack>
      );
    }
    if (row.kind === "more") {
      return (
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Twisty row={row} />
          <Box component="span" sx={{ color: tokens.textMuted, fontSize: "0.8125rem", fontWeight: 400 }}>
            {row.label}
          </Box>
        </Stack>
      );
    }
    if (row.kind === "summary") return <Box component="span">{row.name}</Box>;
    return (
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <Twisty row={row} />
        <Box component="span" sx={{ fontWeight: row.depth === 0 ? 600 : 500 }}>{row.name}</Box>
      </Stack>
    );
  }

  if (col.key === "lead") {
    if (row.kind === "person") return <Box sx={{ color: tokens.textMuted, fontWeight: 400 }}>{row.role}</Box>;
    if (row.kind === "more") return null;
    // A vacancy is set in the muted italic rather than flagged — the same
    // statement the dashed node makes in the chart above.
    if (!row.lead) {
      const vacancy = row.kind === "unit" && row.unitKind === "department";
      return (
        <Box sx={{ color: tokens.textMuted, fontWeight: 400, ...(vacancy ? { fontStyle: "italic" } : {}) }}>
          {vacancy ? VACANT : "—"}
        </Box>
      );
    }
    return <Box sx={{ color: tokens.textMuted, fontWeight: 400 }}>{row.lead.name}</Box>;
  }

  if (col.key === "people") {
    // A person has no count of their own — they are the unit of counting.
    if (row.kind === "person") return <Box sx={{ color: tokens.textInactive }}>—</Box>;
    if (row.kind === "more") return null;
    return <Box sx={numberSx}>{row.headcount}</Box>;
  }

  if (col.key === "contains") {
    if (row.kind === "person") {
      return row.starts ? (
        <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted, fontWeight: 400 }}>{row.starts}</Box>
      ) : null;
    }
    if (row.kind === "unit" && row.contains) {
      return <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted, fontWeight: 400 }}>{row.contains}</Box>;
    }
    return null;
  }
  return null;
};

/* ── screen ────────────────────────────────────────────────────────────── */

const toggleSx = (active) => ({
  borderRadius: "6px",
  p: "0.4375rem 0.875rem",
  fontSize: "0.8125rem",
  fontWeight: active ? 600 : 500,
  cursor: "pointer",
  border: "none",
  background: active ? tokens.primary : "transparent",
  color: active ? tokens.onPrimary : tokens.textMuted,
  transition: (theme) =>
    theme.transitions.create(["background-color", "color"], { duration: theme.transitions.duration.shortest }),
});

const OrgChart = () => {
  const navigate = useNavigate();
  const employees = useOrgEmployees();
  const departments = useOrgDepartments();
  const groups = useOrgGroups();

  const [view, setView] = useState("chart");
  const [expanded, setExpanded] = useState(() => new Set());

  const loading = employees.loading || departments.loading || groups.loading;
  const error = employees.error || departments.error || groups.error;
  const reload = () => {
    employees.refresh();
    departments.refresh();
    groups.refresh();
  };

  const { root } = useMemo(
    () =>
      buildOrgTree({
        employees: employees.data || [],
        departments: departments.data || [],
        teams: groups.data || [],
      }),
    [employees.data, departments.data, groups.data]
  );

  const counts = useMemo(() => countOrgTree(root), [root]);
  const rows = useMemo(() => flattenOrgTree(root, { expanded }), [root, expanded]);
  const unitIds = useMemo(() => allUnitIds(root), [root]);
  const allOpen = unitIds.length > 0 && unitIds.every((idValue) => expanded.has(idValue));

  const toggleUnit = (row) => {
    if (row.kind !== "unit" || !row.expandable) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.add(row.id);
      return next;
    });
  };

  const openUnit = (unit) => {
    setView("list");
    setExpanded((prev) => new Set(prev).add(unit.id));
  };

  const summary = [
    `${counts.departments} ${counts.departments === 1 ? "department" : "departments"}`,
    counts.teams ? `${counts.teams} ${counts.teams === 1 ? "team" : "teams"}` : null,
    `${counts.people} ${counts.people === 1 ? "person" : "people"}`,
  ].filter(Boolean).join(" · ");

  const body = () => {
    if (loading && !employees.data) return <SectionLoader />;
    if (error && !employees.data) {
      return <EmptyState variant="error" title="Couldn't load the organisation" onRetry={reload} />;
    }
    if (!counts.people) {
      return (
        <EmptyState
          title="No people yet"
          description="Add people and departments to see how the organisation is put together."
          actionLabel="Add a department"
          onAction={() => navigate("/app/organization/departments")}
        />
      );
    }
    if (view === "chart") return <ChartView root={root} onOpenUnit={openUnit} />;
    return (
      <>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: "1.125rem" }}>
          <Box sx={{ flex: 1, height: "1px", background: tokens.border }} />
          <Box
            component="button"
            type="button"
            onClick={() => setExpanded(allOpen ? new Set() : new Set(unitIds))}
            sx={{
              all: "unset", cursor: "pointer", fontSize: "0.8125rem", color: tokens.textMuted,
              fontWeight: 500, "&:hover": { textDecoration: "underline" },
            }}
          >
            {allOpen ? "Collapse all" : "Expand all"}
          </Box>
        </Stack>
        <TreeTable
          ariaLabel="Organisation structure"
          columns={COLUMNS}
          rows={rows}
          indent={INDENT_PX}
          renderCell={listCell}
          rowVariant={(row) =>
            row.kind === "summary" ? "total" : row.kind === "unit" && row.depth === 0 ? "band" : "plain"
          }
          onRowClick={toggleUnit}
          empty={{ title: "No departments yet" }}
        />
      </>
    );
  };

  return (
    <Box>
      <Stack direction="row" alignItems="flex-start" spacing={2.5} sx={{ mb: "1.75rem" }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 700, color: tokens.text, mb: "0.25rem" }}>
            Structure
          </Box>
          <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted }}>{summary}</Box>
        </Box>
        <Stack
          direction="row"
          spacing={1}
          role="group"
          aria-label="View"
          sx={{ border: `1px solid ${tokens.borderStrong}`, borderRadius: "8px", p: "3px" }}
        >
          {["chart", "list"].map((v) => (
            <Box
              key={v}
              component="button"
              type="button"
              aria-pressed={view === v}
              onClick={() => setView(v)}
              sx={toggleSx(view === v)}
            >
              {v === "chart" ? "Chart" : "List"}
            </Box>
          ))}
        </Stack>
        <PrimaryButton onClick={() => navigate("/app/organization/departments")}>
          New department
        </PrimaryButton>
      </Stack>

      {body()}
    </Box>
  );
};

export default OrgChart;
